"use client";

import React, { useState } from "react";
import { DPOS_FIGMA_ASSETS } from "./dposFigmaAssets";

export type PaymentMethod = "88pay" | "crypto" | "paylink";

interface PaymentMethodStepProps {
  amount: string;
  onSelect: (method: PaymentMethod) => void;
  onBack: () => void;
  onClose: () => void;
}

interface PaymentMethodOption {
  id: PaymentMethod;
  label: string;
  hasCryptoIcon?: boolean;
}

const PAYMENT_METHODS: PaymentMethodOption[] = [
  { id: "88pay", label: "88Pay" },
  { id: "crypto", label: "Crypto", hasCryptoIcon: true },
  { id: "paylink", label: "Pay Link" },
];

export default function PaymentMethodStep({
  onSelect,
  onBack,
  onClose,
}: PaymentMethodStepProps) {
  const [selected, setSelected] = useState<PaymentMethod>("88pay");

  const handleSelect = (method: PaymentMethod) => {
    setSelected(method);
    onSelect(method);
  };

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <button
        onClick={onBack}
        className="absolute left-8 top-8 border border-[#EBE9EF] rounded-full w-[48px] h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
      >
        <img src={DPOS_FIGMA_ASSETS.backNav} alt="Back" className="h-4 w-4" />
      </button>
      <button
        onClick={onClose}
        className="absolute right-8 top-8 border border-[#EBE9EF] rounded-full w-[48px] h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
      >
        <img
          src={DPOS_FIGMA_ASSETS.closeDelete}
          alt="Close"
          className="h-4 w-4"
        />
      </button>

      <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col gap-6 items-center text-center w-full pb-4">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            Select payment method
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51]">
            Choose how your customer will pay
          </p>
        </div>

        {/* Payment methods label */}
        <div className="w-full pb-6">
          <h3 className="text-[20px] font-semibold leading-[28px] text-[#6D6584]">
            Address book
          </h3>
        </div>

        {/* Payment method options */}
        <div className="flex flex-col gap-2 items-center w-full pb-12">
          {PAYMENT_METHODS.map((method) => {
            const isSelected = selected === method.id;
            return (
              <button
                key={method.id}
                onClick={() => handleSelect(method.id)}
                className={`w-full rounded-[16px] transition-colors ${
                  isSelected ? "p-px" : "border border-[#CECBD7] bg-white"
                }`}
                style={
                  isSelected
                    ? {
                        backgroundImage:
                          "linear-gradient(90deg, #57249C 0%, #EB3366 100%)",
                      }
                    : undefined
                }
              >
                <span
                  className={`flex h-[68px] w-full items-center justify-center gap-2 rounded-[15px] text-[16px] font-semibold leading-[24px] text-[#423E51] ${
                    isSelected ? "bg-white" : ""
                  }`}
                >
                  {method.hasCryptoIcon ? (
                    <span className="flex items-center">
                      <img
                        src={DPOS_FIGMA_ASSETS.cryptoBtc}
                        alt="BTC"
                        className="h-[22px] w-[22px]"
                      />
                      <img
                        src={DPOS_FIGMA_ASSETS.cryptoEth}
                        alt="ETH"
                        className="-ml-[5px] h-5 w-5"
                      />
                    </span>
                  ) : null}
                  {method.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
